import React, { FC, useEffect, useState } from 'react';
import { WizardStepLayout } from '../../../wizard/wizard-summary-layout';
import { useSelector } from 'react-redux';
import { MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from '../../../../constants/command-status';
import commands from '../../../../constants/commands';
import { DEF_PG_SIZE_WIZARD } from '../../../../constants/maximum-page-size';
import { HardwareSubCmds } from '../../../../constants/sub-commands';
import { useLocale } from '../../../../contexts/localeContext';
import { TableContextProvider } from '../../../../contexts/table/TableContextProvider';
import { TranslationKeys } from '../../../../contexts/TranslationKeys';
import { useIcaWebsocketContext } from '../../../../contexts/websocket/IcaWebsocketContext';
import columns from '../../../../models/ColumnDefinitions/InstallPeripheralColumn';
import { Controller } from '../../../../models/Controller';
import { ExecuteCommand } from '../../../../models/ExecuteCommand';
import { ListData } from '../../../../models/ListData';
import { ListQuery } from '../../../../models/ListQuery';
import { PaginationModel } from '../../../../models/Pagination';
import searchColumns from '../../../../models/search/InstallPeripheralsSearch';
import { executeCommand } from '../../../../services/executeCommandService';
import { getErrorMessage } from '../../../../services/utilities/ServiceError';
import { IAppState } from '../../../../store/types';
import ActivityFrame from '../../../activity-frame/activity-frame';
import { useDialog } from '../../../dialog/DialogContext';
import TableTab from '../../../tabs/table-tab';
import {colonDashRegex} from "../../../../utilities/regex";

interface InstallPeripheralProps {
    selectedDevice: Controller;
    onNext: (e) => void;
    onBack: (e) => void;
    setSelectedDevice: (controller: Controller) => void;
}

interface DiscoveredPeripherals{
    id: string
    fixedAddress: string;
}

const InstallPeripheral: FC<InstallPeripheralProps> = ({ onNext, onBack, selectedDevice, setSelectedDevice }) => {
    const { translate } = useLocale();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [busy, setBusy] = useState(false);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const title = translate.byKey(TranslationKeys.selectPeripherals);
    const [discoveredPeripherals, setDiscoveredPeripherals] = useState<DiscoveredPeripherals[]>([]);
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage);
        }
    }, [icaWebSocket.connectionState])

    const onMessage = (data) => {
        
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);

            if (response.command === MONITORING_COMMAND.MONITORING_DISCOVERY) {
                const discoveredPeripherals: DiscoveredPeripherals[] = []
                if (response.attributes.discovery && response.attributes.discovery.length > 0) {
                    response.attributes.discovery[0].scanResults.map((item, index) => {
                        discoveredPeripherals.push({ id: index.toString(), fixedAddress: item.attributes.address })
                    })
                }
                
                setDiscoveredPeripherals(discoveredPeripherals)
                setBusy(false);
            }
        }
    }

    useEffect(() => {
        dataRefresh((listParameters?: ListQuery) => getPeripherals(listParameters));
    }, [discoveredPeripherals])

    /*
        Requests a scan of RS485, Cluster-bus and S-bus.
    */
    const scanBus = () => {
        const command: ExecuteCommand = {
            cmd: commands.Discovery.label,
            cmdSubType: HardwareSubCmds.Scan_Rs485,
            fixedAddress: selectedDevice.fixedAddress,
            macAddress: selectedDevice.macAddress ? selectedDevice.macAddress.replace(colonDashRegex, '') : "",
            type: selectedDevice.typeName,
            userLoginId: user.id,
            jsonResponse: ""
        }

        setBusy(true);
        executeCommand(command)
            .then(() => {
                // Devices will be reported by websocket
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                setBusy(false);
            })
            .finally(() => {
                // Let the on message handle the disposing of the busy loader
            })
    }

    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });

    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const getPeripherals = (listParameters?: ListQuery): Promise<ListData> => {
        /**
            When on page 1 start slicing from 0
            When on page 2 start slicing from page size (10, 50, 100) otherwise use default 10
            When on page above 2 start slicing from page size + page size otherwise use default 10
         */

        const startIndex = listParameters?.goToPage === 1 ? 0 : listParameters?.goToPage === 2 ? (listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD) : (listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD) + (listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD)
        const endIndex = (listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD) * (listParameters?.goToPage ?? 1);

        const dataWithSearchApplied = discoveredPeripherals.filter(item => item[listParameters?.searchTerm?.accessor ?? 'fixedAddress'].toUpperCase().includes((listParameters?.searchTerm?.value ?? '').toUpperCase()));
        const dataWithPaginationApplied = dataWithSearchApplied.slice(startIndex , endIndex)
        const totalDevices = listParameters?.searchTerm?.value ? dataWithPaginationApplied.length : discoveredPeripherals.length;

        const pagination: PaginationModel = {
            maxPageSize: listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD,
            pageNumber: listParameters?.goToPage ?? 1,
            totalCount: totalDevices,
            totalPages: Math.ceil(totalDevices / (listParameters?.pageSize ?? DEF_PG_SIZE_WIZARD))
        }

        return new Promise((resolve, reject) => {
            return resolve({
                rows: dataWithPaginationApplied,
                pagination: pagination
            });
        });
    }

    const handleNext = (e) => {
        const selectedPeripherals: string[] = [];
        discoveredPeripherals.map((peripherals) => {
            if (selectedIds.includes(peripherals.id)){
                selectedPeripherals.push(peripherals.fixedAddress)
            }
        })
        setSelectedDevice({...selectedDevice, selectedPeripherals: selectedPeripherals})
        onNext(e)
    }

    return (
        <WizardStepLayout
            onBack={onBack}
            onNext={handleNext}
            tertiaryActions={
                <button
                    className="secondary secondary-tertiary py-1 px-5"
                    type="button"
                    onClick={scanBus}>
                    {translate.byKey(TranslationKeys.scanBus)}
                </button>
            }>
            <ActivityFrame busy={busy} busyMessage={translate.byKey(TranslationKeys.discoveryMessage)} className={`flex ${busy ? 'h-full w-full justify-center' : ''} flex-wrap content-center`}>
                <div className='flex w-full' style={{ maxHeight: '410px' }}>
                    <TableContextProvider
                        useFilter
                        usePagination
                        useTable
                        useSort
                        useSearch
                        dataMethod={getPeripherals}
                        tableDescriptor={columns}
                        searchDescriptor={searchColumns}
                        manualDataRefresh={manualDataRefresh}
                        useLegend
                        canExportReport={false}>
                        <TableTab
                            title={title}
                            selectRows={true}
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            cyName='installPeripheral'/>
                    </TableContextProvider>
                </div>
            </ActivityFrame>
        </WizardStepLayout>
    );
}

export default InstallPeripheral;
